import { withPageContainerStyles } from '@/common/components/page-layout';
import Spacer from '@/common/components/spacer';
import PDPSectionContainer, { PDPSectionContainerProps } from './pdp-section-container';
import FlexcarWordmark2024 from '@/icons/flexcar-wordmark-2024';
import { colors } from '@/theme/colors';
import Rule from '@/common/components/rule';
import CheckSmall from '@/icons/check-small';

const inspectionItems = [
  'Exterior',
  'Interior',
  'Engine',
  'Mechanical',
  'Operational Safety Systems',
];

interface InspectionItemProps {
  title: string;
  last: boolean;
}

function InspectionItem({ title, last }: InspectionItemProps) {
  return (
    <>
      <div className="flex flex-row items-center justify-between">
        <p className="body2-tight flex-1">{title}</p>
        <CheckSmall
          className="h-[14px] w-[14px] flex-shrink-0"
          fillPrimary={colors.green40}
          variant="mqiCheck"
        />
      </div>
      {!last ? (
        <>
          <Spacer variant="height16" />
          <div className="h-0 w-full border-t border-dashed border-flexcar-neutral70"></div>
          <Spacer variant="height16" />
        </>
      ) : null}
    </>
  );
}
interface InspectionProps
  extends Pick<PDPSectionContainerProps, 'onInViewChange' | 'addSectionRef'> {
  cdnUrl: string;
}

export default function Inspection({
  onInViewChange,
  addSectionRef,
  cdnUrl,
}: InspectionProps) {
  return (
    <PDPSectionContainer
      sectionKey="inspection"
      onInViewChange={onInViewChange}
      addSectionRef={addSectionRef}
    >
      <div className="flex w-full flex-row justify-center bg-flexcar-neutral90">
        <div
          className={withPageContainerStyles(
            'max-w-[1440px] items-center justify-start px-[24px] py-[88px] md:py-[120px]'
          )}
        >
          <img
            src={`${cdnUrl}/web/images/pdp/brandmark-inspection.webp`}
            alt="Inspection Brandmark"
            className="h-[134px] w-[134px] flex-shrink-0"
            fetchPriority="auto"
            loading="lazy"
          />
          <Spacer variant="height24" />
          <h2 className="header-sans-tight hidden text-center text-flexcar-neutral1 md:block">
            Multi-point inspection
          </h2>
          <h3 className="header-sans-tight block text-center text-flexcar-neutral1 md:hidden">
            Multi-point inspection
          </h3>
          <Spacer variant="height24" />
          <p className="body-tight text-center text-[18px] text-flexcar-neutral1">
            Every Flexcar undergoes a detailed inspection to ensure safety, performance,
            and reliability.
            <br className="hidden md:block" /> We check every detail so you can drive with
            confidence.
          </p>

          <Spacer variant="height40" hideForBreakpoints={['md', 'lg', 'xl', '2xl']} />
          <Spacer variant="height56" hideForBreakpoints={['sm']} />

          <div className="flex flex-col gap-[24px] md:flex-row">
            {/** Left image */}
            <img
              src={`${cdnUrl}/web/images/pdp/inspector-meryl.webp`}
              alt="Flexcar technicians inspecting a car"
              className="h-auto w-full flex-shrink-0 rounded-[32px] md:w-1/2 md:object-cover md:object-right xl:h-[450px] xl:w-auto"
              fetchPriority="auto"
              loading="lazy"
            />
            {/** Right Copy */}
            <div className="relative flex flex-col rounded-[32px] py-[40px] md:w-1/2 xl:w-[466px]">
              <img
                src={`${cdnUrl}/web/images/pdp/bg-paper.webp`}
                alt="Paper texture background"
                className="absolute inset-0 z-0 h-full w-full rounded-[32px]"
                fetchPriority="auto"
                loading="lazy"
              />
              <div className="z-1 flex w-full flex-col items-center px-[24px]">
                <FlexcarWordmark2024
                  className="h-[32px] w-[85px]"
                  fillPrimary={colors.blue40}
                />
                <Spacer variant="height8" />
                <p className="homepage-hero-body2 text-[20px] leading-[24px]">
                  Vehicle Inspection Report
                </p>
              </div>
              <Spacer variant="height40" />
              <Rule
                direction="horizontal"
                className="z-1 bg-flexcar-ruleGray shadow-[0px_1px_1px_0px_#FFF]"
              />
              <div className="z-1 flex flex-row items-center justify-between px-[24px] py-[8px]">
                <p className="body-strong flex-1 leading-[22px] text-flexcar-neutral1/60">
                  Items Inspected
                </p>
                <p className="body-strong flex-shrink-0 whitespace-nowrap leading-[22px] text-flexcar-neutral1/60">
                  PASSED
                </p>
              </div>
              <Rule
                direction="horizontal"
                className="z-1 bg-flexcar-ruleGray shadow-[0px_1px_1px_0px_#FFF]"
              />
              <Spacer variant="height24" />
              <div className="z-1 flex w-full flex-col px-[24px]">
                {inspectionItems.map((item, index) => (
                  <InspectionItem
                    key={item}
                    title={item}
                    last={index === inspectionItems.length - 1}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </PDPSectionContainer>
  );
}
